import { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ColorPicker, Text } from '@reveni-io/reveni-ui'

import { availableLanguagesForUser } from '@/common/constants'
import AppContext from '@/common/context/app.context'
import { getCurrentAvailableLanguage } from '@/configuration/helpers/helpers'
import CodeWidget from '@/configuration/pages/ConfigurationPage/ExtrasConfiguration/CodeWidget/CodeWidget'
import CustomText from '@/configuration/pages/ConfigurationPage/ExtrasConfiguration/CustomText/CustomText'

import ProductOptions from './ProductOptions/ProductOptions'
import WidgetPreview from './WidgetPreview/WidgetPreview'
import * as Styles from './styles'

const SizingWidgetConfiguration = ({
  supportedLanguages,
  backgroundColor,
  setBackgroundColor,
  textColor,
  setTextColor,
  useCustomTextTooBig,
  setUseCustomTextTooBig,
  customTextTooBig,
  setCustomTextTooBig,
  useCustomTextTooSmall,
  setUseCustomTextTooSmall,
  customTextTooSmall,
  setCustomTextTooSmall,
  productTaxonomyDisplay,
  setProductTaxonomyDisplay,
  productTaxonomyIds,
  setProductTaxonomyIds,
  productCategories,
  scriptExample,
}) => {
  const { t, i18n } = useTranslation()
  const currentLanguage = getCurrentAvailableLanguage(supportedLanguages, i18n.language)
  const [selectedLanguageTooBig, setSelectedLanguageTooBig] = useState(currentLanguage)
  const [selectedLanguageTooSmall, setSelectedLanguageTooSmall] = useState(currentLanguage)
  const { info } = useContext(AppContext)

  const getAvailableLanguages = () => {
    return availableLanguagesForUser.filter(lang => supportedLanguages?.includes(lang))
  }

  const handleChangeCustomTextTooBig = (value, lang) => {
    setCustomTextTooBig(translations => ({ ...translations, [lang]: value }))
  }

  const handleChangeCustomTextTooSmall = (value, lang) => {
    setCustomTextTooSmall(translations => ({ ...translations, [lang]: value }))
  }

  const handleCheckUseCustomTextTooBig = e => {
    setUseCustomTextTooBig(e.target.checked)
  }

  const handleCheckUseCustomTextTooSmall = e => {
    setUseCustomTextTooSmall(e.target.checked)
  }

  return (
    <Styles.Container>
      <Text type="titleM">{t('Widget de tallas')}</Text>
      <Text type="bodyMRegular">
        {t(
          'Añade nuestro widget de tallas a las páginas de tus productos para reducir las devoluciones. Mostrará un mensaje cada vez que un artículo tenga más devoluciones de lo habitual relacionadas con la talla, y recomendará de forma inteligente a los clientes que elijan una talla más grande o más pequeña de la que suelen usar.'
        )}
      </Text>
      <Styles.ColorsContainer>
        <Text type="titleS">{t('Colores del widget')}</Text>
        <Styles.ColorsPickerContainer>
          <ColorPicker
            onChange={setBackgroundColor}
            label={t('Color del fondo')}
            type="colorPreview"
            defaultColor={backgroundColor}
          />
          <ColorPicker
            onChange={setTextColor}
            label={t('Color del texto')}
            type="colorPreview"
            defaultColor={textColor}
          />
        </Styles.ColorsPickerContainer>
      </Styles.ColorsContainer>
      <Text type="titleS">{t('Texto del widget')}</Text>
      <CustomText
        languages={getAvailableLanguages()}
        onChangeCustomText={handleChangeCustomTextTooBig}
        customTexts={customTextTooBig}
        onCheckUseCustomText={handleCheckUseCustomTextTooBig}
        useCustomText={useCustomTextTooBig}
        supportedLanguages={supportedLanguages}
        title={t('Talla grande')}
        switchLabel={t('Usar texto personalizado para productos que tallen grande')}
        textLanguage={selectedLanguageTooBig}
        setTextLanguage={setSelectedLanguageTooBig}
      />
      <CustomText
        languages={getAvailableLanguages()}
        onChangeCustomText={handleChangeCustomTextTooSmall}
        customTexts={customTextTooSmall}
        onCheckUseCustomText={handleCheckUseCustomTextTooSmall}
        useCustomText={useCustomTextTooSmall}
        supportedLanguages={supportedLanguages}
        title={t('Talla pequeño')}
        switchLabel={t('Usar texto personalizado para productos que tallen pequeño')}
        textLanguage={selectedLanguageTooSmall}
        setTextLanguage={setSelectedLanguageTooSmall}
      />
      <ProductOptions
        productTaxonomyDisplay={productTaxonomyDisplay}
        setProductTaxonomyDisplay={setProductTaxonomyDisplay}
        productTaxonomyIds={productTaxonomyIds}
        setProductTaxonomyIds={setProductTaxonomyIds}
        productCategories={productCategories}
      />
      <Styles.PreviewsContainer>
        <Text type="titleS">{t('Previsualización')}</Text>
        <WidgetPreview
          backgroundColor={backgroundColor}
          textColor={textColor}
          customTextTooBig={useCustomTextTooBig ? customTextTooBig : null}
          customTextTooSmall={useCustomTextTooSmall ? customTextTooSmall : null}
          loadScriptContainerId="reveni-preview-too-big"
          widgetContainerId="reveni-sizing-widget-too-big"
          title={t('Talla grande')}
          sizingRecommendation={'too_big'}
          language={selectedLanguageTooBig}
        />
        <WidgetPreview
          backgroundColor={backgroundColor}
          textColor={textColor}
          customTextTooBig={useCustomTextTooBig ? customTextTooBig : null}
          customTextTooSmall={useCustomTextTooSmall ? customTextTooSmall : null}
          loadScriptContainerId="reveni-preview-too-small"
          widgetContainerId="reveni-sizing-widget-too-small"
          title={t('Talla pequeño')}
          sizingRecommendation={'too_small'}
          language={selectedLanguageTooSmall}
        />
      </Styles.PreviewsContainer>
      <CodeWidget store={info.selectedStore} scriptInfo={scriptExample} />
    </Styles.Container>
  )
}

export default SizingWidgetConfiguration
